import React, {
	useState,
	useRef,
	useCallback
} from "react";
import useFetchData from "../../Shared/FetchData/FetchData";
import {
	Paper,
	Box,
	Typography,
	Button,
	Dialog,
	DialogActions,
	DialogContent,
	DialogContentText,
	DialogTitle,
	Slide,
	TextField,
	MenuItem,
	FormControl,
	CircularProgress,
} from "@mui/material";
import { DataContext } from "../../../App";
import { capitalizeFirstLetter } from "../../../utils/text";
import { DeleteButton } from "../../Shared/Buttons/Buttons";
import { Auth } from "aws-amplify";
import { convertToBase64String } from "../../../utils/file";

export default function UploadATemplatePanel({ seller }) {

	const [state, setState] = useState({
		selectedWarehouseID: "",
		selectedFile: null,
		fileUploaded: false,
		formSubmitted: false,
		enableUploadTemplateButton: false,
		uploadResponseSuccess: {},
		uploadResponseErrors: {},
		showModal: false,
		modalLoading: false,
		modalTitle: "",
		modalBody1: "",
		modalBody2: "",
	});

	const context = React.useContext(DataContext);
	const warehouseAPI = useFetchData(useCallback(() => context.dataProvider.getSellerWarehouses(seller), [context.dataProvider, seller]));

	const warehouseSelectObj = {
		resultsData: warehouseAPI.results,
		status: warehouseAPI.status,
		isLoading: warehouseAPI.isLoading,
	};

	const onChangeGetSelectedWarehouseID = (id) => {
		setState(previousState => {
			return {
				...previousState,
				selectedWarehouseID: id,
			};
		});
	};

	const WarehouseSelect = () => {
		return (
			<FormControl sx={{ m: 0, minWidth: 200 }}>
				<TextField
					id="select"
					label="Select warehouse"
					variant="outlined"
					margin="normal"
					size="small"
					required
					defaultValue=""
					value={state.selectedWarehouseID}
					onChange={(e) => onChangeGetSelectedWarehouseID(e.target.value)}
					select
				>
					{(warehouseSelectObj.resultsData.map(({ bezos_system_id, name }, index) => (
						<MenuItem key={index} value={bezos_system_id}>
							{name}
						</MenuItem>
					)))}
				</TextField>
			</FormControl>
		);
	};

	const isUploadButtonDisabled = () => {
		if (state.fileUploaded === true
			&& state.formSubmitted === false
			&& state.selectedWarehouseID !== "") {
			return false;
		} else {
			return true;
		}
	};

	const onClickDeleteUploadFile = () => {
		uploadInputRef.current.value = "";
		setState(previousState => {
			return {
				...previousState,
				selectedFile: null,
				fileUploaded: false,
			};
		});
	};

	const uploadInputRef = useRef();
	const onChangeGetFileUpload = (e) => {
		setState(previousState => {
			return {
				...previousState,
				selectedFile: e.target.files[0],
				fileUploaded: true,
				enableUploadTemplateButton: true,
				formSubmitted: false,
			};
		});
	};

	const createOrdersFromFileViaWebsocket = async (sellerID, warehouseID, file) => {
		const data = await Auth.currentSession();
		const ws = new WebSocket(process.env.REACT_APP_WEBSOCKET_URL + `?token=${data.accessToken.jwtToken}`);
		const fileDataQueue = [];
		let errors = {};

		ws.onmessage = (event) => {
			const data = JSON.parse(event.data);

			if (data.errors && Object.keys(data.errors).length !== 0) {
				errors = data.errors;

				setState(previousState => {
					return {
						...previousState,
						uploadResponseSuccess: data.successes,
						uploadResponseErrors: data.errors,
						modalTitle: "Unable to upload orders",
						modalBody1: "The following errors were encountered while uploading:",
						modalBody2: data.errors,
					};
				});
			} else if (data.message === "upload complete") {
				ws.close();

				if (errors && Object.keys(errors).length !== 0) {
					setState(previousState => {
						return {
							...previousState,
							modalTitle: "Unable to upload orders",
							modalBody1: "The following errors were encountered while uploading:",
							modalBody2: errors,
						};
					});
				} else {
					setState(previousState => {
						return {
							...previousState,
							modalTitle: "Successfully uploaded all orders",
							modalBody1: "All orders have been successfully uploaded",
							modalBody2: "Note: It can take a few minutes for these orders to appear in your order summary",
						};
					});
				}
			} else if (data.message === "file data received") {
				let fileData = fileDataQueue.shift();
				if (fileData === undefined) {
					fileData = "";
				}

				ws.send(JSON.stringify({
					"action": "import_orders",
					"data": {
						"warehouse_id": warehouseID,
						"seller": sellerID,
						"file_name": file.name,
						"file_data": fileData,
					}
				}));
			} else if (data.message) {
				ws.close();

				setState(previousState => {
					return {
						...previousState,
						modalTitle: "Unable to upload orders",
						modalBody1: "An internal error occurred, please contact Bezos support.",
					};
				});
			}
		};

		ws.onerror = () => {
			ws.close();

			setState(previousState => {
				return {
					...previousState,
					modalTitle: "Unable to upload orders",
					modalBody1: "An internal error occurred, please contact Bezos support.",
				};
			});
		};

		ws.onopen = async () => {
			const chunkSize = 30000;
			const base64File = await convertToBase64String(file);

			setState(previousState => {
				return {
					...previousState,
					showModal: true,
					modalTitle: "Processing File",
					modalBody1: file.name,
					formSubmitted: true,
				};
			});

			for (let start = 0; start < base64File.length; start += chunkSize) {
				const base64Chunk = base64File.slice(start, start + chunkSize);
				fileDataQueue.push(base64Chunk);
			}

			ws.send(JSON.stringify({
				"action": "import_orders",
				"data": {
					"warehouse_id": warehouseID,
					"seller": sellerID,
					"file_name": file.name,
					"file_data": fileDataQueue.shift(),
				}
			}));
		};
	};

	const onClickUploadTemplate = async () => {
		if (!seller) return;

		await createOrdersFromFileViaWebsocket(seller, state.selectedWarehouseID, state.selectedFile);

		setState(previousState => {
			return {
				...previousState,
				modalLoading: false,
				selectedWarehouseID: "",
				selectedFile: null,
				fileUploaded: false,
				enableUploadTemplateButton: false,
			};
		});
		uploadInputRef.current.value = ""; // clear file upload input
	};

	const SubmitPageModalAlert = () => {
		const handleClose = () => {
			setState(previousState => {
				return {
					...previousState,
					showModal: false,
					modalTitle: "",
					modalBody1: "",
					modalBody2: "",
					uploadResponseErrors: {},
					uploadResponseSuccess: {},
				};
			});
		};

		const Transition = React.forwardRef(function Transition(props, ref) {
			return <Slide direction="up" ref={ref} {...props} />;
		});

		return (
			<Dialog
				open={state.showModal}
				TransitionComponent={Transition}
				keepMounted
				onClose={handleClose}
				aria-describedby="alert-dialog-slide-description"
				fullWidth={true}
				maxWidth="sm"
			>
				{(state.modalLoading === true) ?
					(<>
						<DialogTitle>Loading...</DialogTitle>
						<DialogContent sx={{ display: "flex", justifyContent: "center" }}>
							<CircularProgress/>
						</DialogContent>
					</>)
					:
					(
						(state.uploadResponseSuccess === null && state.uploadResponseErrors === null) ?
							(<>
								<DialogTitle>Unable to upload orders</DialogTitle>
								<DialogContent>
									<DialogContentText id="alert-dialog-slide-description" component="div">
										No orders found
									</DialogContentText>
								</DialogContent>
								<DialogActions>
									<Button
										variant="contained"
										onClick={handleClose}
									>
										Confirm
									</Button>
								</DialogActions>
							</>)
							:
							(<>
								<DialogTitle>{state.modalTitle}</DialogTitle>
								<DialogContent>
									<DialogContentText id="alert-dialog-slide-description" component="div">
										{state.modalBody1}
									</DialogContentText>
									{(typeof state.uploadResponseErrors === "object" && state.uploadResponseErrors != null) ?
										(<DialogContentText id="alert-dialog-slide-description" component="div">
											<ul>
												{Object.values(state.uploadResponseErrors).map((value, index) => (
													<li key={index}>
														{capitalizeFirstLetter(value)}
													</li>
												))}
											</ul>
										</DialogContentText>)
										:
										(<DialogContentText id="alert-dialog-slide-description" component="div">
											<br/>{state.modalBody2}
										</DialogContentText>)
									}
								</DialogContent>
								<DialogActions>
									<Button
										variant="contained"
										onClick={handleClose}
									>
										Confirm
									</Button>
								</DialogActions>
							</>)
					)
				}
			</Dialog>
		);
	};

	return (
		<Paper sx={{ p: 2, width: "100%" }} elevation={2}>
			<Box mb={1}>
				<Typography variant="h5" component="h2" mb={2}>
					Upload Template
				</Typography>
			</Box>

			<Box mb={0}>
				<Typography component="p" sx={{ fontWeight: "bold" }}>
					Select a warehouse
				</Typography>
			</Box>
			<Box mb={1}>
				<WarehouseSelect/>
			</Box>

			<Box mb={3}>
				<input
					type="file"
					name="File Upload"
					accept='text/csv'
					ref={uploadInputRef}
					onChange={(e) => onChangeGetFileUpload(e)}
				/>
				{(state.fileUploaded === true) &&
					<Button title="Remove file">
						<DeleteButton
							onClick={() => onClickDeleteUploadFile()}
							icon={true}
						/>
					</Button>
				}
			</Box>

			<Box mb={1}>
				<Button
					variant="contained"
					component="span"
					onClick={() => onClickUploadTemplate()}
					disabled={isUploadButtonDisabled()}
				>
					Upload
				</Button>
			</Box>

			<SubmitPageModalAlert/>
		</Paper>
	);
}